import { forwardRef } from "react";
import ButtonToggle, { ButtonToggleProps } from "../ButtonToggle";
import { WithAriaLabelEnforcement } from "./types";
import { DataTrackingId } from "../../types";
import { useOverFlowItemContext } from "./internal/ToolbarItemOverflowContext";
import Tooltip from "../Tooltip";
import { ToolbarItemWrapper } from "./internal/ToolbarItemWrapper";
import { useTrackingId } from "../../hooks";
import { useToolbarSize } from "./internal/ToolbarContext";

import cx from "classnames";
import styles from "./Toolbar.module.scss";

/**
 * Base props for the ToolbarButtonToggle component
 * @extends Omit<ButtonToggleProps, "aria-label" | "children">
 */
type ToolbarButtonToggleBaseProps = Omit<
  ButtonToggleProps,
  "aria-label" | "children"
>;

/**
 * Props for the ToolbarButtonToggle component
 * @extends WithAriaLabelEnforcement<ToolbarButtonToggleBaseProps>
 */
export type ToolbarButtonToggleProps =
  WithAriaLabelEnforcement<ToolbarButtonToggleBaseProps> & DataTrackingId;

/**
 * ToolbarButtonToggle component for toggleable actions within a toolbar.
 *
 * Features:
 * - Toggle state management (on/off)
 * - Designed specifically for toolbar integration
 * - Consistent styling with other toolbar items
 * - Automatic tooltip for icon-only toggles
 * - Overflow menu support
 * - Accessibility enforcement for aria-labels
 * - Visual feedback for toggle state
 * - Proper ARIA attributes for toggle semantics
 *
 * @example
 * <ToolbarButtonToggle icon={BoldIcon} aria-label="Bold" checked={isBold} />
 *
 * @example
 * <ToolbarButtonToggle checked={isItalic}>
 *   Italic
 * </ToolbarButtonToggle>
 */
export const ToolbarButtonToggle = forwardRef<
  HTMLButtonElement,
  ToolbarButtonToggleProps
>((props, _ref) => {
  const { className, children, ...rest } = props;

  // Checks if this component is rendered inside the overflow menu and fallbacks when the context is unavailable
  const { isInOverflowMenu = false } = useOverFlowItemContext?.() || {};
  const size = useToolbarSize();

  const trackingId = useTrackingId({
    name: "ToolbarButtonToggle",
    data: {
      checked: props.checked,
      disabled: props.disabled,
      "aria-label": props["aria-label"],
      children: props.children,
    },
    hasOverride: !!props["data-tracking-id"],
  });

  const buttonToggleClassNames = cx(
    className,
    styles["toolbar-button-item"],
    styles["toolbar-button-toggle-item"],
  );

  const item = children ? (
    <ButtonToggle
      data-tracking-id={trackingId}
      size={size}
      data-anv="toolbar-button-toggle"
      className={buttonToggleClassNames}
      {...rest}
    >
      {children}
    </ButtonToggle>
  ) : (
    <Tooltip>
      <Tooltip.Trigger>
        <ButtonToggle
          data-tracking-id={trackingId}
          size={size}
          data-anv="toolbar-button-toggle"
          className={buttonToggleClassNames}
          {...rest}
        />
      </Tooltip.Trigger>
      <Tooltip.Content>{rest["aria-label"]}</Tooltip.Content>
    </Tooltip>
  );

  if (isInOverflowMenu) {
    return item;
  }

  return (
    <ToolbarItemWrapper item={{ itemProps: props, itemType: "buttonToggle" }}>
      {item}
    </ToolbarItemWrapper>
  );
});

ToolbarButtonToggle.displayName = "ToolbarButtonToggle";
